import { useEffect, useMemo, useState } from 'react'
import {
  FRACOES_RADIANTES,
  bolaDeFogo,
  doseTermicaTDU,
  estadoNoTempo,
  massaNoTanqueKg,
  type Ambiente,
} from '../../../dominio/bleve'
import { perfilParaEstado, volumeTanqueM3 } from '../../../dominio/caminhaoTanque/perfil'
import { NOMES_USO, PREDIOS_BEIRA_RODOVIA, distanciaAoPredioM } from '../../../dominio/cenario/predios'
import { produtoPorId, type IdProduto, type Produto } from '../../../dominio/produtos'
import { formatarDistancia } from '../../../dominio/unidades'
import { VENTO_PADRAO } from '../../../dominio/vento'
import { NOMES_ZONA, leituraNoPonto, zonasPorRadiacao, type IdZona } from '../../../dominio/zonas'
import { CaminhaoTanque3D } from '../../../visualizacao3d/caminhaoTanque/CaminhaoTanque3D'
import type { VolumeEnquadramento } from '../../../visualizacao3d/camera/comandosCamera'
import { Biruta3D } from '../../../visualizacao3d/cenario/Biruta3D'
import { Pista3D } from '../../../visualizacao3d/cenario/Pista3D'
import { PredioBloco3D } from '../../../visualizacao3d/cenario/PredioBloco3D'
import { Terreno3D } from '../../../visualizacao3d/cenario/Terreno3D'
import { AneisZona3D, type AnelZona } from '../../../visualizacao3d/zonas/AneisZona3D'
import { BolaDeFogo3D } from '../../../visualizacao3d/zonas/BolaDeFogo3D'
import { ControlesTimeline } from '../../ControlesTimeline'
import { IconeCamadas, IconeCena, IconeModuloBleve, IconeReproduzir, IconeResultados } from '../../icones'
import type { ConteudoInspetor } from '../../modulos/tipos'
import { useTimeline } from '../../useTimeline'
import { CascaOficina, type ContextoCasca } from '../../workspace/CascaOficina'
import { BarraTimeline } from '../comum/BarraTimeline'
import { LegendaZonas } from '../comum/LegendaZonas'
import { SeletorProduto } from '../comum/SeletorProduto'
import { ValorCitado } from '../comum/ValorCitado'

export const CORES_ZONA: Record<IdZona, string> = { quente: '#ef4444', morna: '#f59e0b', fria: '#22c55e' }
export const COR_GRE = '#60a5fa'

type CenarioRuptura = keyof typeof FRACOES_RADIANTES

const ROTULO_RUPTURA: Record<CenarioRuptura, string> = {
  abaixoDoAlivio: 'Ruptura abaixo da pressão de alívio (Frad 0,25)',
  tipica: 'Valor intermediário (Frad 0,30)',
  acimaDoAlivio: 'Ruptura acima da pressão de alívio — tanque aquecido no fogo (Frad 0,40)',
}

/** BLEVE com bola de fogo só faz sentido aqui para gás liquefeito inflamável. */
function motivoSemBleve(produto: Produto): string | null {
  if (produto.estadoTransporte !== 'gasLiquefeitoPressurizado') {
    return produto.estadoTransporte === 'gasComprimido'
      ? 'Gás comprimido: o cilindro pode romper, mas não forma a bola de fogo de um gás liquefeito.'
      : 'Este laboratório modela a bola de fogo só de gás liquefeito inflamável.'
  }
  if (!produto.calorCombustaoMJKg || !produto.liiPorcento) {
    return 'Não inflamável: a ruptura não forma bola de fogo; o risco é a nuvem tóxica e os fragmentos.'
  }
  return null
}

const fmt = (valor: number, casas = 1) => valor.toLocaleString('pt-BR', { maximumFractionDigits: casas, minimumFractionDigits: casas })

/**
 * Oficina BLEVE e zonas: a massa no tanque vira bola de fogo (CCPS), a bola
 * vira radiação no chão e a radiação vira anéis — e os anéis viram ZQ/ZM/ZF
 * e prédios destacados. O isolamento do GRE aparece ao lado, para o aluno
 * ver que o cálculo não o substitui.
 */
export function OficinaBleve({ contexto }: { contexto: ContextoCasca }) {
  const [idProduto, setIdProduto] = useState<IdProduto>('glp')
  const [enchimento, setEnchimento] = useState(0.85)
  const [ruptura, setRuptura] = useState<CenarioRuptura>('acimaDoAlivio')
  const [ambiente, setAmbiente] = useState<Ambiente>({ temperaturaC: 25, umidadeRelativa: 0.7 })
  const [mostrarLimiares, setMostrarLimiares] = useState(true)
  const [mostrarGre, setMostrarGre] = useState(true)
  const [idPredio, setIdPredio] = useState<string | null>(null)

  const produto = produtoPorId(idProduto)
  const perfil = useMemo(() => perfilParaEstado(produto.estadoTransporte), [produto.estadoTransporte])
  const volumeTanque = volumeTanqueM3(perfil.tanque)
  const massaKg = massaNoTanqueKg(volumeTanque, enchimento, produto.densidadeLiquidoKgM3!.valor)
  const bola = useMemo(
    () => bolaDeFogo({ massaKg, calorCombustaoMJKg: produto.calorCombustaoMJKg!.valor, fracaoRadiante: FRACOES_RADIANTES[ruptura] }),
    [massaKg, produto, ruptura],
  )
  const isolamentoGreM = produto.gre.isolamentoTanqueEmFogoM.valor
  const zonas = useMemo(() => zonasPorRadiacao(bola, ambiente, isolamentoGreM), [bola, ambiente, isolamentoGreM])
  const timeline = useTimeline(bola.duracaoS)
  const estadoBola = estadoNoTempo(bola, timeline.tempo)

  const predios = useMemo(
    () =>
      PREDIOS_BEIRA_RODOVIA.map((predio) => {
        const leitura = leituraNoPonto(bola, ambiente, zonas, distanciaAoPredioM(predio))
        return { predio, leitura, doseTDU: doseTermicaTDU(leitura.fluxoKWm2, bola.duracaoS) }
      }).sort((a, b) => a.leitura.distanciaM - b.leitura.distanciaM),
    [bola, ambiente, zonas],
  )

  const raioReferencia = Math.max(zonas.raioMornaM, 400)
  // Enquadramento padrão: o tamanho da bola de fogo no instante atual, não a
  // Zona Morna térmica (centenas de metros) — a câmera abre perto do
  // caminhão e se afasta acompanhando a bola crescer, não a zona de calor.
  const raioInicialM = Math.max(35, estadoBola.diametroM * 0.9)
  const volume = useMemo<VolumeEnquadramento>(
    () => ({
      comprimento: raioReferencia * 2.2,
      largura: raioReferencia * 2.2,
      altura: bola.alturaCentroM + bola.diametroM / 2,
      alturaAlvoM: 1.5,
      raioInicialM,
    }),
    // arredonda para não reenquadrar a cada ponto do deslizante / cada frame da animação
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [Math.round(raioReferencia / 50), Math.round(bola.diametroM / 10), Math.round(raioInicialM / 5)],
  )

  // Acompanhamento automático: enquanto ninguém pegou a órbita manualmente, a
  // câmera reenquadra sozinha a cada passo do raio acima — o `restaurar` já
  // anima suavemente (ControleCamera3D), então chamadas em sequência durante
  // a animação da bola de fogo viram um afastamento cinematográfico.
  const [seguindoCamera, setSeguindoCamera] = useState(true)
  const { executar } = contexto.camera
  useEffect(() => {
    if (seguindoCamera) executar('restaurar')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Math.round(raioInicialM / 5)])
  useEffect(() => {
    if (timeline.tempo === 0) setSeguindoCamera(true)
  }, [timeline.tempo])

  const aneis: AnelZona[] = [
    { id: 'zq', raioM: zonas.raioQuenteM, cor: CORES_ZONA.quente, preenchido: true, opacidade: 0.22, rotulo: `ZQ · ${formatarDistancia(zonas.raioQuenteM)}`, subrotulo: '≥ 5 kW/m²', varianteRotulo: 'quente' },
    { id: 'zm', raioM: zonas.raioMornaM, cor: CORES_ZONA.morna, preenchido: true, opacidade: 0.14, rotulo: `ZM · ${formatarDistancia(zonas.raioMornaM)}`, subrotulo: '≥ 1,6 kW/m²', varianteRotulo: 'morna' },
    ...(mostrarLimiares
      ? zonas.aneis
          .filter((a) => a.raioM != null && a.limiar.kWm2 > 5)
          // do lado oposto aos rótulos das zonas: anéis internos são pequenos e os rótulos colidiriam
          .map((a, i): AnelZona => ({ id: `l${a.limiar.kWm2}`, raioM: a.raioM!, cor: '#fca5a5', tracejado: true, rotulo: `${fmt(a.limiar.kWm2)} kW/m²`, subrotulo: a.limiar.rotulo, varianteRotulo: 'risco', anguloRotuloRad: i === 0 ? Math.PI * 0.6 : Math.PI }))
      : []),
    ...(mostrarGre ? [{ id: 'gre', raioM: isolamentoGreM, cor: COR_GRE, tracejado: true, rotulo: `GRE · ${formatarDistancia(isolamentoGreM)}`, subrotulo: 'isolar o público', varianteRotulo: 'neutro' as const }] : []),
  ]

  const corDoPredio = (zona: IdZona, dentroGre: boolean): string | null =>
    zona === 'quente' ? CORES_ZONA.quente : zona === 'morna' ? CORES_ZONA.morna : mostrarGre && dentroGre ? COR_GRE : null

  const selecionado = predios.find((p) => p.predio.id === idPredio) ?? null
  const inspetor: ConteudoInspetor | null = selecionado && {
    titulo: selecionado.predio.nome,
    subtitulo: `${NOMES_USO[selecionado.predio.uso]} · ${NOMES_ZONA[selecionado.leitura.zona].nome}`,
    propriedades: [
      { rotulo: 'Distância ao tanque', valor: formatarDistancia(selecionado.leitura.distanciaM) },
      { rotulo: 'Radiação no prédio', valor: `${fmt(selecionado.leitura.fluxoKWm2)} kW/m² durante ${fmt(bola.duracaoS)} s` },
      { rotulo: 'Dose térmica', valor: <ValorCitado fonte="eisenberg1975">{fmt(selecionado.doseTDU, 0)} TDU</ValorCitado> },
      {
        rotulo: 'Para uma pessoa exposta',
        valor: selecionado.leitura.dentroDaBola
          ? 'Envolvida pela bola de fogo.'
          : selecionado.leitura.limiarAtingido?.efeitoPessoa ?? 'Abaixo de 1,6 kW/m²: sem desconforto.',
      },
      {
        rotulo: 'Para a edificação',
        valor: selecionado.leitura.limiarAtingido?.efeitoEdificacao ?? 'Nenhum efeito da radiação.',
      },
      {
        rotulo: 'Isolamento do GRE',
        valor: selecionado.leitura.dentroDoIsolamentoGre
          ? `Dentro dos ${formatarDistancia(isolamentoGreM)}: evacuar, mesmo fora das zonas de radiação (fragmentos).`
          : 'Fora do isolamento do GRE.',
      },
    ],
    aoFechar: () => setIdPredio(null),
  }

  const grupos = [
    {
      id: 'cenario',
      rotulo: 'Cenário',
      icone: <IconeModuloBleve />,
      resumo: `${fmt(massaKg / 1000)} t de ${produto.nome}`,
      conteudo: () => (
        <div className="grupo-controle">
          <SeletorProduto idSelecionado={idProduto} aoSelecionar={setIdProduto} motivoIndisponivel={motivoSemBleve} />
          <label className="deslizante">
            <span>
              Enchimento do tanque <strong>{Math.round(enchimento * 100)}%</strong> · {fmt(massaKg / 1000)} t ({fmt(volumeTanque)} m³ × {fmt(produto.densidadeLiquidoKgM3!.valor, 0)} kg/m³)
            </span>
            <input type="range" min={0.1} max={0.95} step={0.05} value={enchimento} onChange={(e) => setEnchimento(Number(e.target.value))} />
          </label>
          <span className="titulo-controle">Como o tanque rompe</span>
          {(Object.keys(ROTULO_RUPTURA) as CenarioRuptura[]).map((chave) => (
            <label key={chave} className="toggle">
              <input type="radio" name="ruptura" checked={ruptura === chave} onChange={() => setRuptura(chave)} />
              {ROTULO_RUPTURA[chave]}
            </label>
          ))}
        </div>
      ),
    },
    {
      id: 'ambiente',
      rotulo: 'Ambiente',
      icone: <IconeCena />,
      conteudo: () => (
        <div className="grupo-controle">
          <label className="deslizante">
            <span>Temperatura do ar <strong>{ambiente.temperaturaC} °C</strong></span>
            <input type="range" min={0} max={40} step={1} value={ambiente.temperaturaC} onChange={(e) => setAmbiente({ ...ambiente, temperaturaC: Number(e.target.value) })} />
          </label>
          <label className="deslizante">
            <span>Umidade relativa <strong>{Math.round(ambiente.umidadeRelativa * 100)}%</strong></span>
            <input type="range" min={0.1} max={1} step={0.05} value={ambiente.umidadeRelativa} onChange={(e) => setAmbiente({ ...ambiente, umidadeRelativa: Number(e.target.value) })} />
          </label>
          <p className="nota-controle">O vapor d’água absorve parte da radiação: ar úmido encurta um pouco os anéis.</p>
        </div>
      ),
    },
    {
      id: 'camadas',
      rotulo: 'Camadas',
      icone: <IconeCamadas />,
      conteudo: () => (
        <div className="grupo-controle">
          <label className="toggle">
            <input type="checkbox" checked={mostrarLimiares} onChange={(e) => setMostrarLimiares(e.target.checked)} />
            limiares de 37,5 e 12,5 kW/m²
          </label>
          <label className="toggle">
            <input type="checkbox" checked={mostrarGre} onChange={(e) => setMostrarGre(e.target.checked)} />
            isolamento do GRE ({formatarDistancia(isolamentoGreM)})
          </label>
        </div>
      ),
    },
    {
      id: 'animacao',
      rotulo: 'Animação',
      icone: <IconeReproduzir />,
      conteudo: () => <ControlesTimeline timeline={timeline} fase={`Bola de fogo: ${fmt(bola.duracaoS)} s`} />,
    },
  ]

  const contagem = (zona: IdZona) => predios.filter((p) => p.leitura.zona === zona).length

  return (
    <CascaOficina
      contexto={contexto}
      oficina={{
        volume,
        cameraLivre: true,
        distanciaMaxima: 6000,
        corFundo: '#26333f',
        neblinaM: [2500, 6000],
        idSelecionado: idPredio ? `predio-${idPredio}` : null,
        nomeSelecionado: selecionado?.predio.nome ?? null,
        aoClicarVazio: () => setIdPredio(null),
        aoInteragirManualCamera: () => setSeguindoCamera(false),
        ferramentas: {
          grupos,
          chips: [
            { id: 'massa', rotulo: 'Massa', valor: `${fmt(massaKg / 1000)} t` },
            { id: 'bola', rotulo: 'Bola', valor: `Ø ${fmt(bola.diametroM, 0)} m · ${fmt(bola.duracaoS)} s` },
            { id: 'zq', rotulo: 'ZQ', valor: formatarDistancia(zonas.raioQuenteM), tom: 'perigo' },
            { id: 'zm', rotulo: 'ZM', valor: formatarDistancia(zonas.raioMornaM), tom: 'alerta' },
          ],
          inspetor,
        },
        operacao: (
          <div className="ficha-produto">
            <section>
              <h3 className="titulo-controle">Bola de fogo</h3>
              <dl className="leituras">
                <div><dt>Massa no tanque</dt><dd>{fmt(massaKg / 1000, 2)} t</dd></div>
                <div><dt>Diâmetro máximo (5,8·M^⅓)</dt><dd><ValorCitado fonte="ccps2000">{fmt(bola.diametroM, 0)} m</ValorCitado></dd></div>
                <div>
                  <dt>Duração ({bola.regime === 'momento' ? '0,45·M^⅓, M < 30 t' : '2,6·M^⅙, M ≥ 30 t'})</dt>
                  <dd><ValorCitado fonte="ccps2010">{fmt(bola.duracaoS)} s</ValorCitado></dd>
                </div>
                <div><dt>Altura do centro (0,75·D)</dt><dd><ValorCitado fonte="ccps2000">{fmt(bola.alturaCentroM, 0)} m</ValorCitado></dd></div>
                <div>
                  <dt>Poder emissivo (Roberts)</dt>
                  <dd>
                    <ValorCitado fonte="ccps2000" observacao={bola.poderEmissivoLimitado ? 'limitado ao teto de 400 kW/m²' : undefined}>
                      {fmt(bola.poderEmissivoKWm2, 0)} kW/m²
                    </ValorCitado>
                  </dd>
                </div>
              </dl>
            </section>
            <section>
              <h3 className="titulo-controle">Zonas (mapeamento didático)</h3>
              <table className="tabela-resultado">
                <thead><tr><th>Zona</th><th className="numero">Até</th><th className="numero">Prédios</th></tr></thead>
                <tbody>
                  {(['quente', 'morna'] as const).map((zona) => (
                    <tr key={zona}>
                      <td><span className={`selo-zona selo-zona--${zona}`}>{NOMES_ZONA[zona].sigla}</span> {NOMES_ZONA[zona].uso}</td>
                      <td className="numero">{formatarDistancia(zona === 'quente' ? zonas.raioQuenteM : zonas.raioMornaM)}</td>
                      <td className="numero">{contagem(zona)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td><span className="selo-zona selo-zona--fria">ZF</span> {NOMES_ZONA.fria.uso}</td>
                    <td className="numero">além</td>
                    <td className="numero">{contagem('fria')}</td>
                  </tr>
                </tbody>
              </table>
            </section>
            <section>
              <h3 className="titulo-controle">Limiares de radiação</h3>
              <table className="tabela-resultado">
                <thead><tr><th>kW/m²</th><th>Pessoa</th><th className="numero">Raio</th></tr></thead>
                <tbody>
                  {zonas.aneis.map(({ limiar, raioM }) => (
                    <tr key={limiar.kWm2}>
                      <td><ValorCitado fonte={limiar.fonte}>{fmt(limiar.kWm2)}</ValorCitado></td>
                      <td>{limiar.efeitoPessoa}</td>
                      <td className="numero">{raioM == null ? '—' : formatarDistancia(raioM)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <p className="aviso-modelo">
              O GRE (guia {produto.guiaGre.valor}) manda isolar <strong>{formatarDistancia(isolamentoGreM)}</strong> em todas as direções
              quando o tanque está envolvido em fogo — por causa dos fragmentos, que o modelo de radiação não calcula. O cálculo
              nunca reduz esse isolamento.
            </p>
          </div>
        ),
        checklist: (
          <div className="grupo-controle">
            <span className="titulo-controle">Prédios por distância</span>
            <table className="tabela-resultado">
              <thead><tr><th>Prédio</th><th className="numero">Dist.</th><th className="numero">kW/m²</th><th>Zona</th></tr></thead>
              <tbody>
                {predios.map(({ predio, leitura }) => (
                  <tr key={predio.id}>
                    <td>
                      <button type="button" className="ligacao" onClick={() => setIdPredio(predio.id)}>{predio.nome}</button>
                    </td>
                    <td className="numero">{formatarDistancia(leitura.distanciaM)}</td>
                    <td className="numero">{fmt(leitura.fluxoKWm2)}</td>
                    <td>
                      <span className={`selo-zona selo-zona--${leitura.zona}`}>{NOMES_ZONA[leitura.zona].sigla}</span>
                      {leitura.zona === 'fria' && leitura.dentroDoIsolamentoGre && <> <span className="selo-zona selo-zona--gre">GRE</span></>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ),
        itensEstado: [
          {
            id: 'simulacao',
            rotulo: 'Bola de fogo',
            valor: timeline.reproduzindo ? 'em andamento' : timeline.tempo > 0 ? 'pausada' : 'antes da ruptura',
            icone: <IconeResultados />,
            tom: timeline.reproduzindo ? 'alerta' : 'neutro',
          },
        ],
        sobreposicao: (
          <>
            <LegendaZonas
              titulo="Zonas — BLEVE"
              itens={[
                { id: 'zq', cor: CORES_ZONA.quente, rotulo: 'ZQ (≥ 5 kW/m²)', valor: formatarDistancia(zonas.raioQuenteM) },
                { id: 'zm', cor: CORES_ZONA.morna, rotulo: 'ZM (1,6–5 kW/m²)', valor: formatarDistancia(zonas.raioMornaM) },
                { id: 'zf', cor: CORES_ZONA.fria, rotulo: 'ZF (< 1,6 kW/m²)' },
                ...(mostrarGre ? [{ id: 'gre', cor: COR_GRE, rotulo: 'Isolamento GRE (público)', valor: formatarDistancia(isolamentoGreM), tracejado: true }] : []),
              ]}
            />
            <BarraTimeline timeline={timeline} duracaoTotal={bola.duracaoS} rotulo="bola de fogo" />
          </>
        ),
        cena: (
          <>
            <Terreno3D ladoM={8000} />
            <Pista3D comprimentoM={5000} />
            <Biruta3D posicao={[-14, 20]} direcaoVentoGraus={VENTO_PADRAO.direcaoGraus} velocidadeMS={VENTO_PADRAO.velocidadeMS} />
            <group position={[1.75, 0, 0]}>
              <CaminhaoTanque3D
                perfil={perfil}
                numeroRisco={produto.numeroRisco.valor}
                onu={produto.onu}
                classes={[produto.classe.valor, ...produto.riscosSubsidiarios]}
              />
            </group>
            <AneisZona3D aneis={aneis} />
            {predios.map(({ predio, leitura }) => (
              <PredioBloco3D
                key={predio.id}
                predio={predio}
                destaque={corDoPredio(leitura.zona, leitura.dentroDoIsolamentoGre)}
                selecionado={predio.id === idPredio}
                mostrarNome={predio.id === idPredio || predio.uso === 'escola' || predio.uso === 'hospital'}
                aoSelecionar={setIdPredio}
              />
            ))}
            <BolaDeFogo3D estado={estadoBola} />
          </>
        ),
      }}
    />
  )
}
